import React, { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../services/context';
import PageHeader from '../../Components/PageHeader';
import request from '../../services/request';
import endPoint from '../../services/endPoint';

/**
 * Interface de gestion des transactions de paiement
 * Basée sur les méthodes de paiement du SubscriptionController
 */
const PaymentTransactionsManager = () => {
  const { user } = useContext(AppContext);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filtres
  const [filters, setFilters] = useState({
    payment_method: '',
    status: '',
    date_from: '',
    date_to: '',
    search: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Formulaire de création de transaction
  const [createForm, setCreateForm] = useState({
    subscription_id: '',
    amount: '',
    currency: 'XOF',
    payment_method: 'orange_money',
    payment_reference: '',
    payment_date: '',
    status: 'pending',
    description: '',
    metadata: {}
  });

  // Méthodes de paiement disponibles (du contrôleur Laravel)
  const paymentMethods = [
    { value: 'orange_money', label: 'Orange Money', icon: '🟠', color: 'warning' },
    { value: 'moov_money', label: 'Moov Money', icon: '🔵', color: 'primary' },
    { value: 'mtn_money', label: 'MTN Mobile Money', icon: '🟡', color: 'warning' },
    { value: 'card', label: 'Carte Bancaire', icon: '💳', color: 'info' },
    { value: 'bank_transfer', label: 'Virement Bancaire', icon: '🏦', color: 'secondary' },
    { value: 'cash', label: 'Espèces', icon: '💵', color: 'success' }
  ];

  const transactionStatuses = [
    { value: 'pending', label: 'En attente', color: 'warning' },
    { value: 'completed', label: 'Terminé', color: 'success' },
    { value: 'failed', label: 'Échec', color: 'danger' },
    { value: 'cancelled', label: 'Annulé', color: 'secondary' },
    { value: 'refunded', label: 'Remboursé', color: 'info' }
  ];

  useEffect(() => {
    loadInitialData();
  }, [currentPage]);

  useEffect(() => {
    applyFilters();
  }, [filters, transactions]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      // Charger les transactions de paiement
      const transactionsRes = await request.get(`${endPoint.baseUrl}/payment-transactions?page=${currentPage}`);
      const transData = transactionsRes.data?.data || [];
      setTransactions(Array.isArray(transData.data) ? transData.data : []);
      setTotalPages(transData.last_page || 1);

      // Charger les abonnements pour le formulaire
      const subscriptionsRes = await request.get(endPoint.subscriptions);
      const subsData = subscriptionsRes.data?.data || [];
      setSubscriptions(Array.isArray(subsData.data) ? subsData.data : Array.isArray(subsData) ? subsData : []);

      toast.success('Données chargées avec succès');
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des données');
      setTransactions([]);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const applyFilters = useCallback(() => {
    let filtered = [...transactions];

    if (filters.payment_method) {
      filtered = filtered.filter(t => t.payment_method === filters.payment_method);
    }

    if (filters.status) {
      filtered = filtered.filter(t => t.status === filters.status);
    }

    if (filters.date_from) {
      filtered = filtered.filter(t => new Date(t.payment_date || t.created_at) >= new Date(filters.date_from));
    }

    if (filters.date_to) {
      filtered = filtered.filter(t => new Date(t.payment_date || t.created_at) <= new Date(filters.date_to));
    }

    if (filters.search) {
      filtered = filtered.filter(t =>
        t.payment_reference?.toLowerCase().includes(filters.search.toLowerCase()) ||
        t.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
        t.subscription?.slug?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
  }, [transactions, filters]);

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    try {
      const response = await request.post(`${endPoint.baseUrl}/payment-transactions`, createForm);
      if (response.data?.success) {
        toast.success('Transaction créée avec succès');
        setShowCreateModal(false);
        resetCreateForm();
        loadInitialData();
      }
    } catch (error) {
      console.error('Erreur création:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création');
    }
  };

  const handleShowDetails = async (transaction) => {
    try {
      const response = await request.get(`${endPoint.baseUrl}/payment-transactions/${transaction.id}`);
      if (response.data?.success) {
        setSelectedTransaction(response.data.data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Erreur détails:', error);
      setSelectedTransaction(transaction);
      setShowDetailsModal(true);
    }
  };

  const handleUpdateStatus = async (transactionId, newStatus) => {
    try {
      const response = await request.put(`${endPoint.baseUrl}/payment-transactions/${transactionId}`, {
        status: newStatus
      });
      if (response.data?.success) {
        toast.success('Statut mis à jour');
        loadInitialData();
        if (showDetailsModal) {
          handleShowDetails({ id: transactionId });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      subscription_id: '',
      amount: '',
      currency: 'XOF',
      payment_method: 'orange_money',
      payment_reference: '',
      payment_date: '',
      status: 'pending',
      description: '',
      metadata: {}
    });
  };

  const getPaymentMethodInfo = (method) => {
    return paymentMethods.find(pm => pm.value === method) || 
           { label: method, icon: '💳', color: 'secondary' };
  };

  const getStatusInfo = (status) => {
    return transactionStatuses.find(s => s.value === status) || 
           { label: status, color: 'secondary' };
  };

  const getStatusBadge = (status) => {
    const statusInfo = getStatusInfo(status);
    return (
      <span className={`badge bg-${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatPrice = (price, currency = 'XOF') => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' ' + currency;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotalAmount = () => {
    return filteredTransactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <PageHeader title="Gestion des Transactions de Paiement" />

      {/* Filtres et Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-2">
                  <label className="form-label">Méthode de Paiement</label>
                  <select
                    className="form-select"
                    value={filters.payment_method}
                    onChange={(e) => setFilters({...filters, payment_method: e.target.value})}
                  >
                    <option value="">Toutes les méthodes</option>
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.icon} {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="">Tous les statuts</option>
                    {transactionStatuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Date début</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.date_from}
                    onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Date fin</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.date_to}
                    onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Recherche</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Référence, description..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                  />
                </div>

                <div className="col-md-2">
                  {user.permissions?.includes('create payments') && (
                    <button
                      className="btn btn-primary w-100"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Nouvelle Transaction
                    </button>
                  )}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-10">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setFilters({ payment_method: '', status: '', date_from: '', date_to: '', search: '' })}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Réinitialiser les filtres
                  </button>
                </div>
                <div className="col-md-2 text-end">
                  <strong className="text-success">
                    Total: {formatPrice(calculateTotalAmount())}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques Rapides */}
      <div className="row mb-4">
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-primary">{transactions.length}</h4>
              <small>Total Transactions</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-success">
                {transactions.filter(t => t.status === 'completed').length}
              </h4>
              <small>Terminées</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-warning">
                {transactions.filter(t => t.status === 'pending').length}
              </h4>
              <small>En attente</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-danger">
                {transactions.filter(t => t.status === 'failed').length}
              </h4>
              <small>Échouées</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-info">
                {transactions.filter(t => t.status === 'refunded').length}
              </h4>
              <small>Remboursées</small>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center">
            <div className="card-body">
              <h4 className="text-success">
                {formatPrice(calculateTotalAmount()).split(' ')[0]}
              </h4>
              <small>Revenus XOF</small>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des Transactions */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                Transactions de Paiement ({filteredTransactions.length})
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>ID/Référence</th>
                      <th>Abonnement</th>
                      <th>Montant</th>
                      <th>Méthode</th>
                      <th>Statut</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map(transaction => (
                      <tr key={transaction.id}>
                        <td>
                          <div>
                            <strong>#{transaction.id}</strong>
                            <br />
                            <small className="text-muted">
                              {transaction.payment_reference || 'Pas de réf.'}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{transaction.subscription?.slug || 'N/A'}</strong>
                            <br />
                            <small className="text-muted">
                              {transaction.subscription?.user?.name || 'Utilisateur inconnu'}
                            </small>
                          </div>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatPrice(transaction.amount, transaction.currency)}
                          </strong>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="me-2">
                              {getPaymentMethodInfo(transaction.payment_method).icon}
                            </span>
                            <span>
                              {getPaymentMethodInfo(transaction.payment_method).label}
                            </span>
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td>
                          <small>
                            {formatDate(transaction.payment_date || transaction.created_at)}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {transaction.description ? 
                              (transaction.description.length > 30 ? 
                                transaction.description.substring(0, 30) + '...' : 
                                transaction.description
                              ) : 
                              'Aucune description'
                            }
                          </small>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-info"
                              onClick={() => handleShowDetails(transaction)}
                              title="Voir détails"
                            >
                              <i className="bi bi-eye"></i>
                            </button>

                            {user.permissions?.includes('update payments') && transaction.status === 'pending' && (
                              <>
                                <button
                                  className="btn btn-outline-success"
                                  onClick={() => handleUpdateStatus(transaction.id, 'completed')}
                                  title="Marquer comme terminé"
                                >
                                  <i className="bi bi-check-circle"></i>
                                </button>

                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => handleUpdateStatus(transaction.id, 'failed')}
                                  title="Marquer comme échoué"
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              </>
                            )}

                            {user.permissions?.includes('refund payments') && transaction.status === 'completed' && (
                              <button
                                className="btn btn-outline-warning"
                                onClick={() => handleUpdateStatus(transaction.id, 'refunded')}
                                title="Rembourser"
                              >
                                <i className="bi bi-arrow-return-left"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredTransactions.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bi bi-credit-card display-1 text-muted"></i>
                    <p className="text-muted mt-2">Aucune transaction trouvée</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer">
                <nav>
                  <ul className="pagination mb-0 justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Précédent
                      </button>
                    </li>
                    
                    {[...Array(totalPages)].map((_, index) => (
                      <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Suivant
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Création */}
      {showCreateModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Créer une Nouvelle Transaction</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleCreateTransaction}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Abonnement *</label>
                        <select
                          className="form-select"
                          value={createForm.subscription_id}
                          onChange={(e) => setCreateForm({...createForm, subscription_id: e.target.value})}
                          required
                        >
                          <option value="">Sélectionner un abonnement</option>
                          {subscriptions.map(sub => (
                            <option key={sub.id} value={sub.id}>
                              {sub.slug} - {sub.user?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Montant *</label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            value={createForm.amount}
                            onChange={(e) => setCreateForm({...createForm, amount: e.target.value})}
                            required
                            min="0"
                            step="0.01"
                          />
                          <select
                            className="form-select"
                            style={{maxWidth: '100px'}}
                            value={createForm.currency}
                            onChange={(e) => setCreateForm({...createForm, currency: e.target.value})}
                          >
                            <option value="XOF">XOF</option>
                            <option value="EUR">EUR</option>
                            <option value="USD">USD</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Méthode de Paiement *</label>
                        <select
                          className="form-select"
                          value={createForm.payment_method}
                          onChange={(e) => setCreateForm({...createForm, payment_method: e.target.value})}
                          required
                        >
                          {paymentMethods.map(method => (
                            <option key={method.value} value={method.value}>
                              {method.icon} {method.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Référence de Paiement</label>
                        <input
                          type="text"
                          className="form-control"
                          value={createForm.payment_reference}
                          onChange={(e) => setCreateForm({...createForm, payment_reference: e.target.value})}
                          placeholder="Ex: TXN123456"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Date de Paiement</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={createForm.payment_date}
                          onChange={(e) => setCreateForm({...createForm, payment_date: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Statut Initial</label>
                        <select
                          className="form-select"
                          value={createForm.status}
                          onChange={(e) => setCreateForm({...createForm, status: e.target.value})}
                        >
                          {transactionStatuses.map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={createForm.description}
                          onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                          placeholder="Description de la transaction..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetCreateForm();
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Créer la Transaction
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal des Détails */}
      {showDetailsModal && selectedTransaction && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Détails de la Transaction #{selectedTransaction.id}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary">Informations de Base</h6>
                    <table className="table table-sm table-borderless">
                      <tbody>
                        <tr>
                          <th>ID:</th>
                          <td>{selectedTransaction.id}</td>
                        </tr>
                        <tr>
                          <th>Référence:</th>
                          <td>{selectedTransaction.payment_reference || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Montant:</th>
                          <td>
                            <strong className="text-success">
                              {formatPrice(selectedTransaction.amount, selectedTransaction.currency)}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <th>Méthode:</th>
                          <td>
                            <span className="me-2">
                              {getPaymentMethodInfo(selectedTransaction.payment_method).icon}
                            </span>
                            {getPaymentMethodInfo(selectedTransaction.payment_method).label}
                          </td>
                        </tr>
                        <tr>
                          <th>Statut:</th>
                          <td>{getStatusBadge(selectedTransaction.status)}</td>
                        </tr>
                        <tr>
                          <th>Date de paiement:</th>
                          <td>{formatDate(selectedTransaction.payment_date)}</td>
                        </tr>
                        <tr>
                          <th>Créé le:</th>
                          <td>{formatDate(selectedTransaction.created_at)}</td>
                        </tr>
                        <tr>
                          <th>Modifié le:</th>
                          <td>{formatDate(selectedTransaction.updated_at)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="col-md-6">
                    <h6 className="text-primary">Informations d'Abonnement</h6>
                    {selectedTransaction.subscription ? (
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <th>Abonnement:</th>
                            <td>{selectedTransaction.subscription.slug}</td>
                          </tr>
                          <tr>
                            <th>Utilisateur:</th>
                            <td>{selectedTransaction.subscription.user?.name}</td>
                          </tr>
                          <tr>
                            <th>Plan:</th>
                            <td>{selectedTransaction.subscription.subscription_plan?.name}</td>
                          </tr>
                          <tr>
                            <th>Statut abonnement:</th>
                            <td>{getStatusBadge(selectedTransaction.subscription.status)}</td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Informations d'abonnement non disponibles
                      </div>
                    )}
                  </div>

                  {selectedTransaction.description && (
                    <div className="col-12 mt-3">
                      <h6 className="text-primary">Description</h6>
                      <div className="border p-3 rounded">
                        {selectedTransaction.description}
                      </div>
                    </div>
                  )}

                  {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
                    <div className="col-12 mt-3">
                      <h6 className="text-primary">Métadonnées</h6>
                      <pre className="bg-light p-3 rounded">
                        {JSON.stringify(selectedTransaction.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                {user.permissions?.includes('update payments') && (
                  <div className="btn-group me-auto">
                    {selectedTransaction.status === 'pending' && (
                      <>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleUpdateStatus(selectedTransaction.id, 'completed')}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Marquer Terminé
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleUpdateStatus(selectedTransaction.id, 'failed')}
                        >
                          <i className="bi bi-x-circle me-1"></i>
                          Marquer Échoué
                        </button>
                      </>
                    )}
                    {selectedTransaction.status === 'completed' && user.permissions?.includes('refund payments') && (
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handleUpdateStatus(selectedTransaction.id, 'refunded')}
                      >
                        <i className="bi bi-arrow-return-left me-1"></i>
                        Rembourser
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentTransactionsManager;
